import { GCInstant as platform } from 'src/lib/gcinstant';
import { isPlatformGuest } from 'src/lib/utils';
import { Feature } from 'src/replicant/analytics';
import * as Inventory from 'src/replicant/inventory';
import * as Social from 'src/replicant/social';
import StateObserver, { user } from 'src/StateObserver';
import { startInstallNativeSequence } from '../native/native.sequence';
import { socialActions } from '../social/social.redux';
import { devSettings } from 'src/lib/settings';
import {
  mapFeatureToDialogOrigin,
  openPopupPromise,
} from 'src/lib/popups/popupOpenClose';
import { isNativePromoAvailable } from '../native/native.util';
import type { CrossplayTracking } from '../crossplay/crossplay.types';
import { NativePromoOrigin } from 'src/replicant/native';
import { startInviteFriendsSequence } from '../social/social.sequence';
import { getGemsToEnergyExchangeRates } from 'src/replicant/inventory/inventory.getters';
import * as Promo from 'src/replicant/promo';
import { match2ShopBundlesOpenShop } from '../minigame/match2-timestep/shopbundles/match2-shopbundles.sequence';
import { isWebIapAvailable } from 'src/lib/iap/iap.util';
import { canAttemptPayments } from 'src/lib/iap/iap.util';
import { DeepLink } from '../launch/deepLinks';
import { puzzleCoinsIsPlayingTutorialDeterministic } from 'src/replicant/puzzlecoins/puzzlecoins.getters';

type SupportedNativePromoOrigin = Extract<
  NativePromoOrigin,
  'gem_shop_icon' | 'gem_plus_button'
>;

export type PinchType = 'energy' | 'bPoints' | 'gems' | 'moves';

export type SupportedOrigins =
  | 'visit_revive'
  | 'match2_team_create'
  | 'match2'
  | 'slots'
  | 'growthcapsule'
  | 'catalog_fusion'
  | 'gacha'
  | SupportedNativePromoOrigin
  | 'gems_on_out_of_resources'
  | 'energy_on_out_of_resources'
  | 'decoration'
  | 'coins_plus_button'
  | 'coins_shop_icon'
  | 'energy_plus_button'
  | 'energy_shop_icon'
  | 'energy_minigame'
  | 'event_energy'
  | 'decor_progression'
  | 'pet_progression'
  | 'redo_evolution';

/**
 * Check if user has minimal amount of gems to purchase item
 */
function hasEnoughGemsForMinimalPurchase(minGems: number) {
  const { user } = StateObserver.getState();
  return Inventory.getters.getGems(user) >= minGems;
}

/**
 * Check if we have ways to get gems for this user
 */
export function hasAlternativeWaysToGetGems(): boolean {
  // Player can purchase gems in shop for money
  const canPurchase = isIapReady();

  // Show invite sequence Feature is available and user hasn't seen invite popup on thi session
  const canInvite = Social.getters.isFriendInviteAvailable(user());

  // Show crossplay promo popup if:
  // - Native promo available
  // - Hasn't seen cross promotion in this session
  const canInstallNativeApp = isNativePromoAvailable();

  return canPurchase || canInvite || canInstallNativeApp;
}

/**
 * Lock gems shop button state
 */
export function isGemsButtonDisabled() {
  // Should be disabled while playing the tutorial
  if (puzzleCoinsIsPlayingTutorialDeterministic(user())) {
    return true;
  }

  const IAPEnabled = isIapReady();
  const isGuestMode = isPlatformGuest(platform);

  // For guest mode always show button
  if (isGuestMode) return false;

  // If any of this feature is active  and IAP is disabled do not lock button
  // but show alternative ways to get gems
  if (!IAPEnabled) {
    // Invite friend to get gems
    if (Social.getters.isFriendInviteAvailable(user())) return false;
  }

  // In case no one from ab test above running resolve it buy IAP ready value
  return !IAPEnabled;
}

/**
 * Lock bPoints shop button state
 */
export function isBPointsButtonDisabled() {
  // Since we resolved bpoints-from-puzzle to enabled, bpoints shop is always disabled
  return true;
}

/**
 * Lock energy button state
 */
export function isEnergyButtonDisabled() {
  // Should be disabled while playing the tutorial
  if (puzzleCoinsIsPlayingTutorialDeterministic(user())) {
    return true;
  }
  return false;
}

/**
 * Is API enabled helper
 * @deprecated Use {@link isPaymentAvailable} from `src/lib/iap/iap.util.ts` instead.
 * Missing LINE IAP and web IAP checks. Default param snapshots state.
 */
export function isIapReady({ iap } = StateObserver.getState()): boolean {
  // Guest mode IAP is only available on iOS.
  if (isPlatformGuest(platform) && platform.osType !== 'IOS_APP') {
    return false;
  }

  return devSettings.get('enableIAP') || iap.ready;
}

/**
 * Open coins shop sequence
 */
export async function openCoinsShopSequence(feature: SupportedOrigins) {
  // Open coins shop
  const status = await openPopupPromise('popupCoinsShop', {
    origin: mapFeatureToDialogOrigin(feature),
  });

  // Player successful purchase items
  if (status === 'success') return;

  // Player closed dialog
  if (status === 'close') return;

  // Player tried to purchase item but he didn't have enough gems
  // Open gems shop or alternative ways for getting gems
  if (status === 'failed') {
    const result = await startGemsPinchSequence({
      feature,
      pinch: 'bPoints',
      crossplayTracking: {
        feature: 'bPoints',
        subFeature: `bPoints_pinch`,
      },
    });

    // Try again
    if (result === 'success') {
      await openCoinsShopSequence(feature);
    }
  }
}

/**
 * Open gems shop sequence
 */
export async function openGemsShopSequence(feature: SupportedOrigins) {
  return startGemsPinchSequence({
    feature,
    pinch: 'gems',
    crossplayTracking: {
      feature: 'shop',
      subFeature: `shop_gems`,
    },
  });
}

/**
 * Call this on gems shop purchase attempt, if even IAP disabled
 * If IAP enabled just show gems shop or alternative way to get gems
 * For guest mode we have another dialog
 *
 * Possible path:
 *  a. If guest mode: promote login to line
 *  b. If IAP enabled: show gems shop popup
 *  c. If no IAP available:
 *    c1. If friends available for invites: Show invite sequence with specific title
 *    c2. If native app available: Show native app promo with specific title
 */
export async function startGemsPinchSequence({
  feature,
  additionalContext,
  pinch,
  gemsNeeded,
  skipAlternativeWaysGetGems,
  crossplayTracking,
}: {
  feature: SupportedOrigins;
  additionalContext?: DeepLink[];
  pinch?: PinchType;
  gemsNeeded?: number;
  skipAlternativeWaysGetGems?: boolean;
  crossplayTracking: CrossplayTracking;
}) {
  const canAttemptIAP = canAttemptPayments(StateObserver.getState().user); // TODO: Remove this aft LIFF improvement testing

  // TODO: resolve to canAttemptIAP = true after LIFF improvement testing
  const gemsShopResult = canAttemptIAP
    ? await match2ShopBundlesOpenShop({
        gemsNeeded,
        additionalContext,
        crossplayTracking,
        origin: mapFeatureToDialogOrigin(feature),
      })
    : 'failed';

  // Return if purchase was successful
  if (gemsShopResult === 'success') return gemsShopResult;

  // Skip alternative ways to get gems
  if (skipAlternativeWaysGetGems) return gemsShopResult;

  // Ignore next actions if user closed shop popup and its not pinch sequence
  if (!pinch && gemsShopResult === 'close') return 'close';

  // Show crossplay promo or/and invite sequence as alternative way get gems
  await showAlternativeWayToGetGems({
    feature,
    additionalContext,
    pinch,
    gemsNeeded,
    respectNativePromoCooldown: gemsShopResult === 'close' && !!pinch,
    alwaysShowInvite: gemsShopResult === 'failed',
  });

  return gemsShopResult;
}

/**
 * Alternative way how to get gems
 *
 * a. If friends available for invites: Show invite sequence
 * b. If native app available: Show native app promo
 *
 * @returns 'close' if user closed the popup, 'success' if user made a purchase. 'failed' should be removed
 */
async function showAlternativeWayToGetGems({
  feature,
  additionalContext,
  pinch,
  gemsNeeded,
  respectNativePromoCooldown,
  alwaysShowInvite,
}: {
  feature: SupportedOrigins;
  additionalContext?: DeepLink[];
  pinch?: PinchType;
  gemsNeeded?: number;
  respectNativePromoCooldown: boolean;
  alwaysShowInvite: boolean;
}): Promise<'close' | 'failed' | 'success'> {
  const { user, social } = StateObserver.getState();
  const { isFriendInviteAvailable } = Social.getters;

  // Show crossplay promo popup if:
  // - Native promo available
  // - Open web payments aren't available
  await shopNativeInstallSequence({
    useCooldown: respectNativePromoCooldown,
    feature,
    gemsNeeded,
    additionalContext,
  });

  // TODO: Remove gemsShopResult === 'failed' after LIFF improvement testing
  const shouldShowInvite = alwaysShowInvite || !social.hasSeenInvitePopup;

  // Show invite sequence Feature is available and user hasn't seen invite popup on thi session
  if (isFriendInviteAvailable(user) && shouldShowInvite) {
    const invited = await shopInviteSequence({ pinch });
    return invited ? 'success' : 'close';
  }

  return 'close';
}

/**
 * Locations:
 *   1. Tap [+] button for Energy
 *   2. Tap Energy icon in Shop Screen
 *
 * Logic:
 * Shows the conversion popup : Gems to Energy
 *  1. Tap Exchange button on a dialog allowing conversion of Gems to Energy
 *    a. If a player doesn’t have enough Gems
 *      -> Shows a Gem purchase dialog
 *    b. If a player has enough Gems
 *      -> Conversion happens
 */
export async function openEnergyShopSequence(feature: SupportedOrigins) {
  // Open energy shop
  const status = await openPopupPromise('popupEnergyShop', {
    origin: mapFeatureToDialogOrigin(feature),
  });

  // Player successful purchase items
  if (status === 'success') return;

  // Player tried to purchase item but he didn't have enough gems
  // Open gems shop or alternative ways for getting gems
  if (status === 'failed') {
    const result = await startGemsPinchSequence({
      feature,
      pinch: 'energy',
      crossplayTracking: {
        feature: 'energy',
        subFeature: `energy_shop`,
      },
    });

    // Show energy shop again
    if (result === 'success') {
      await openEnergyShopSequence(feature);
    }
  }
}

/**
 * Energy pinch sequence
 *
 * Triggered:
 * 1. Running out of energy after playing a minigame/slot
 * 2. When a player doesn’t have enough Energy and taps a mini-game/spin icon to try to play
 * when user try to use energy but he don't have enough energy
 *
 * Logic:
 * If a player has enough Gems, show the conversion popup : Gems to Energy with title “Run out of Energy!”
 * If a player doesn’t have enough Gems, show the Gems Pinch Sequence
 * If the player converts, then exit the Gems Pinch Sequence, and start the Energy Pinch Sequence
 */
export async function startEnergyPinchSequence(feature: SupportedOrigins) {
  const minGems = getGemsToEnergyExchangeRates(user())[0].premium;

  // If has enough gems for minimal purchase
  // Show energy shop with alternative title
  if (hasEnoughGemsForMinimalPurchase(minGems)) {
    const result = await openPopupPromise('popupEnergyShop', {
      pinch: 'energy',
      origin: mapFeatureToDialogOrigin(feature),
    });

    if (result === 'success' || result === 'close') {
      // User don't want to buy energy
      // Or buy energy successfully
      return;
    }
  }

  // If user don't have enough gems for minimal purchase
  // Start Gem Pinch Sequence if all above are not converted
  const result = await startGemsPinchSequence({
    feature,
    pinch: 'energy',
    crossplayTracking: {
      feature: 'energy',
      subFeature: `energy_pinch`,
    },
  });

  // If user bought gems, start energy pinch sequence again
  if (result === 'success') {
    await startEnergyPinchSequence(feature);
  }
}

/**
 * Show crossplay promo popup if:
 * - Native promo available
 * - Open web payments aren't available
 *
 * @param ops
 * @returns
 */
async function shopNativeInstallSequence(ops: {
  useCooldown: boolean;
  feature: SupportedOrigins;
  gemsNeeded?: number;
  additionalContext?: DeepLink[];
}) {
  const { useCooldown, feature, gemsNeeded, additionalContext } = ops;
  const { user } = StateObserver.getState();
  const now = StateObserver.now();

  const isOpenWeb = isWebIapAvailable();

  // Respect cooldown or not, sometimes we want to ignore CD
  const showCrossplayPromo = useCooldown
    ? Promo.getters.isPromoReady(user, 'crossPlayPromo', now)
    : true;

  // Exit if open web
  if (isOpenWeb) return;

  // Exit if native promo is not available
  if (!isNativePromoAvailable()) return;

  // If we useCooldown and crossPlayPromo on cooldown exit here
  if (!showCrossplayPromo) return;

  // Mark popup as shown.
  StateObserver.dispatch(socialActions.setHasSeenCrossplayPopup(true));

  // Only trigger promo CD if we're taking cooldown into account.
  if (useCooldown) {
    await StateObserver.invoke.promoWasShown('crossPlayPromo');
  }

  // Start native install sequence
  await startInstallNativeSequence({
    originFeature: 'low_gem_balance',
    pinch: 'gems',
    feature,
    gemsNeeded,
    additionalContext,
  });
}

/**
 * Start invite sequence for shop pinch
 *
 * @param opts
 * @returns
 */
async function shopInviteSequence(opts: { pinch: PinchType | undefined }) {
  const { pinch } = opts;

  // Mark popup as shown.
  StateObserver.dispatch(socialActions.setHasSeenInvitePopup(true));

  // Start sequence
  const invited = await startInviteFriendsSequence(
    Feature.INVITE._,
    Feature.INVITE.INVITE_INSTEAD_GEMS_SHOP,
    pinch,
  );

  return (invited?.length ?? 0) > 0;
}
